import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle, ChevronLeft, CheckCircle2, RotateCcw, FileText,
  Loader2, Inbox, Clock, User, Hash, Building2,
} from "lucide-react";
import AvidaLogo from "@/components/brand/AvidaLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "pending",         label: "Pending review",   filter: "pending" },
  { id: "approved",        label: "Reviewed — OK",    filter: "approved" },
  { id: "needs_revision",  label: "Needs revision",   filter: "needs_revision" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function ageDays(iso) {
  if (!iso) return null;
  const ts = new Date(iso).getTime();
  if (!ts) return null;
  return Math.floor((Date.now() - ts) / 86400000);
}

export default function QaQueue() {
  const [tab, setTab] = useState("pending");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reviewerName, setReviewerName] = useState(() => {
    try { return localStorage.getItem("wi-pa-qa-reviewer") || ""; } catch { return ""; }
  });
  const [savingId, setSavingId] = useState(null);
  const [noteFor, setNoteFor] = useState({}); // { recordId: "free-text note" }

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        low_change_density: "true",
        qa_review: tab,
        limit: "100",
      });
      const r = await axios.get(`${API}/audit-records?${params.toString()}`);
      setRecords(r.data?.audit_records || r.data?.records || []);
    } catch (e) {
      toast.error(`Failed to load: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const saveReviewer = (v) => {
    setReviewerName(v);
    try { localStorage.setItem("wi-pa-qa-reviewer", v); } catch { /* ignore */ }
  };

  const decide = async (record, decision) => {
    if (!reviewerName.trim()) {
      toast.error("Enter your name (top right) first — every QA decision is logged with the reviewer.");
      return;
    }
    setSavingId(record.id);
    try {
      const r = await axios.post(`${API}/audit-records/${record.id}/qa-review`, {
        decision,
        reviewer_name: reviewerName.trim(),
        note: (noteFor[record.id] || "").trim(),
      });
      toast.success(`Marked ${decision === "approved" ? "OK" : "needs revision"} ✓`);
      // Optimistic: remove from pending tab.
      setRecords((rs) => rs.filter((x) => x.id !== record.id));
      setNoteFor((n) => { const cp = { ...n }; delete cp[record.id]; return cp; });
      return r.data;
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const counts = useMemo(() => ({ all: records.length }), [records]);

  return (
    <div className="min-h-screen bg-hero text-[#0B1E3A]" data-testid="qa-queue-page">
      {/* Header */}
      <div className="px-6 lg:px-12 pt-6 pb-3 border-b border-[#E7EEF3] bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-[#3A5275] hover:text-[#0B1E3A]" aria-label="Home" data-testid="qa-home">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <AvidaLogo size="sm" />
            <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#8A6B00]">QA review queue</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              value={reviewerName}
              onChange={(e) => saveReviewer(e.target.value)}
              placeholder="Your name (logged on every decision)"
              className="tb-input !h-9 w-[280px]"
              data-testid="qa-reviewer-name"
            />
          </div>
        </div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1E3A] mt-3 tracking-tight">
          Low-change re-cert review
        </h1>
        <p className="text-[13px] text-[#3A5275] mt-1 max-w-2xl">
          Re-certs flagged because the RN made very few changes from the previous visit (under
          the rubber-stamp threshold). Review each one and decide whether the assessment was
          genuinely thorough or needs revision.
        </p>
      </div>

      {/* Tabs */}
      <div className="px-6 lg:px-12 pt-5">
        <div className="inline-flex gap-1 p-1 rounded-xl bg-white border border-[#D6E3EF]" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 h-9 rounded-lg text-[13px] font-semibold transition ${
                tab === t.id
                  ? "bg-[#0A3D79] text-white shadow-sm"
                  : "text-[#3A5275] hover:bg-[#f4f6f1]"
              }`}
              data-testid={`qa-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {!loading && (
          <span className="ml-3 text-[12px] text-[#3A5275]" data-testid="qa-result-count">
            {counts.all} {tab === "pending" ? "awaiting review" : "in this view"}
          </span>
        )}
      </div>

      {/* List */}
      <div className="px-6 lg:px-12 py-6 space-y-4 max-w-4xl">
        {loading && (
          <div className="flex items-center gap-2 text-[#3A5275]" data-testid="qa-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {!loading && records.length === 0 && (
          <div className="card-surface !p-10 text-center" data-testid="qa-empty">
            <Inbox className="w-10 h-10 mx-auto text-[#B7CADA] mb-3" />
            <div className="font-display text-[18px] font-semibold text-[#0B1E3A]">
              {tab === "pending" ? "Queue is clear" : "Nothing in this view yet"}
            </div>
            <div className="text-[13px] text-[#3A5275] mt-1.5 max-w-md mx-auto">
              {tab === "pending"
                ? "No re-certs are awaiting QA review. Nice work."
                : "Reviewed re-certs will appear here once an admin records a decision."}
            </div>
          </div>
        )}

        {!loading && records.map((r) => {
          const cd = r.change_density || {};
          const days = ageDays(r.generated_at);
          const isPending = tab === "pending";
          return (
            <article
              key={r.id}
              className="card-surface !p-5"
              data-testid={`qa-row-${r.id}`}
            >
              <header className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-[#FFFDEC] border border-[#DBB90A] text-[#8A6B00] text-[11px] font-semibold">
                      <AlertTriangle className="w-3 h-3" /> {cd.total_changes ?? 0} of ≥{cd.threshold ?? 3} required changes
                    </span>
                    {days !== null && (
                      <span className="inline-flex items-center gap-1 h-6 px-2 rounded-full bg-white border border-[#D6E3EF] text-[#3A5275] text-[11px] font-semibold">
                        <Clock className="w-3 h-3" /> {days}d ago
                      </span>
                    )}
                    {r.qa_review?.decision && (
                      <span className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-semibold border ${
                        r.qa_review.decision === "approved"
                          ? "bg-[#E7F1FB] border-[#0A3D79] text-[#0A3D79]"
                          : "bg-[#fbeae5] border-[#c05746] text-[#8a2e1a]"
                      }`}>
                        {r.qa_review.decision === "approved" ? "Reviewed — OK" : "Needs revision"}
                        {r.qa_review.reviewer_name ? ` · ${r.qa_review.reviewer_name}` : ""}
                      </span>
                    )}
                  </div>
                  <div className="font-display text-[18px] font-semibold mt-2 text-[#0B1E3A]">
                    {r.client_name || "Unnamed client"}
                  </div>
                  <div className="text-[12.5px] text-[#3A5275] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {r.rn_name || "—"}</span>
                    <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {r.agency_name || "—"}</span>
                    {r.client_member_id && (
                      <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" /> {r.client_member_id}</span>
                    )}
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(r.generated_at)}</span>
                  </div>
                </div>
                <a
                  href={`${API}/audit-records/${r.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-ghost !h-9 inline-flex items-center gap-1"
                  data-testid={`qa-pdf-${r.id}`}
                >
                  <FileText className="w-4 h-4" /> Audit receipt
                </a>
              </header>

              {/* Change-density breakdown */}
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
                <CountBox label="Diagnoses"   n={cd.diagnoses ?? 0} />
                <CountBox label="Medications" n={cd.medications ?? 0} />
                <CountBox label="ADL levels"  n={cd.adl ?? 0} />
                <CountBox label="Narratives"  n={cd.narrative ?? 0} />
              </div>

              {isPending && (
                <div className="mt-4">
                  <textarea
                    value={noteFor[r.id] || ""}
                    onChange={(e) => setNoteFor((n) => ({ ...n, [r.id]: e.target.value }))}
                    placeholder="Optional reviewer note (e.g. 'Spoke with RN; vitals unchanged confirmed.' or 'Send back — missing wound check.')"
                    className="tb-input !h-[64px] !py-2 leading-snug w-full"
                    data-testid={`qa-note-${r.id}`}
                  />
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <button
                      type="button"
                      disabled={savingId === r.id}
                      onClick={() => decide(r, "approved")}
                      className="btn-primary !h-9 inline-flex items-center gap-1 disabled:opacity-60"
                      data-testid={`qa-approve-${r.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4" /> Reviewed — OK
                    </button>
                    <button
                      type="button"
                      disabled={savingId === r.id}
                      onClick={() => decide(r, "needs_revision")}
                      className="h-9 px-3 rounded-lg border border-[#c05746] text-[#8a2e1a] bg-white hover:bg-[#fbeae5] inline-flex items-center gap-1 text-[13px] font-semibold disabled:opacity-60"
                      data-testid={`qa-reject-${r.id}`}
                    >
                      <RotateCcw className="w-4 h-4" /> Needs revision
                    </button>
                    {savingId === r.id && (
                      <span className="inline-flex items-center gap-1 text-[#3A5275] text-[12px]">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
                      </span>
                    )}
                  </div>
                </div>
              )}

              {!isPending && r.qa_review?.note && (
                <div className="mt-3 px-3 py-2 rounded-lg bg-[#f4f6f1] border border-[#D6E3EF] text-[12.5px] text-[#1E2E4A]">
                  <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-[#8A6B00] mr-1.5">Reviewer note</span>
                  {r.qa_review.note}
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}

function CountBox({ label, n }) {
  return (
    <div className="px-3 py-2 rounded-lg bg-[#f4f6f1] border border-[#D6E3EF]">
      <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#3A5275]">{label}</div>
      <div className={`text-[15px] font-bold ${n === 0 ? "text-[#c05746]" : "text-[#0B1E3A]"}`}>{n}</div>
    </div>
  );
}
